import { Injectable } from '@angular/core';
import { UserConfiguration } from '@brokerportal/common/models';

const WIDGET_URL = 'https://salesiq.zoho.com/widget';
const SCRIPT_ELEMENT_ID = 'zsiqscript';
const WIDGET_ELEMENT_ID = 'zsiqwidget';
const WIDGET_POLL_INTERVAL_MS = 500;
const WIDGET_POLL_TIMEOUT_MS = 10000;
const WIDGET_RESET_DELAY_MS = 1500;

/**
 * Owns the Zoho SalesIQ chat widget lifecycle.
 *
 * SalesIQ persists the visitor identity it was last given in its own browser storage, so the widget
 * has to be reset before a different person is identified — otherwise the operator console keeps
 * showing the previous visitor. We track who we last identified in `VisitorKeyStorageKey` and reset
 * whenever that changes, which covers every way the current user can change without the previous
 * session being logged out cleanly: legacy login, FusionAuth login, and staff impersonation
 * starting or ending. Resetting on identity change (rather than on login) also means a plain page
 * reload leaves an in-progress conversation alone.
 */
@Injectable()
export class SalesIqService {
    static readonly VisitorKeyStorageKey = 'rs_bp_salesiq_visitor';

    /**
     * Injects the widget script and identifies `user` to SalesIQ once it is ready.
     * `host` is the element the script and widget placeholder are appended to.
     *
     * Idempotent: calling it again for a host that already has the widget is a no-op, so a caller
     * that runs twice cannot end up with two scripts racing two `afterReady` callbacks.
     */
    load(user: UserConfiguration, host: HTMLElement) {
        // `$zoho` is defined by assets/js/sales-iq.js and carries the widget code. Without it there
        // is no widget to drive.
        if (!this.$zoho || !host) {
            return;
        }

        if (host.querySelector(`#${SCRIPT_ELEMENT_ID}`)) {
            return;
        }

        this.insertScript(host, user);
        this.insertWidgetPlaceholder(host);

        this.$zoho.salesiq?.floatbutton?.visible('show');
    }

    hide() {
        this.$zoho?.salesiq?.floatbutton?.visible('hide');
        this.$zoho?.salesiq?.floatwindow?.visible('hide');
    }

    /**
     * Clears the identity SalesIQ retains for the departing user. Never throws: a widget failure
     * must not block logout.
     */
    resetOnLogout() {
        try {
            this.writeVisitorKey(null);

            if (window['$ZSIQWidget'] !== undefined) {
                this.reset();
            }
        } catch (error) {
            console.error('Error resetting Zoho chat widget:', error);
        }
    }

    private get $zoho(): any {
        return window['$zoho'];
    }

    private insertScript(host: HTMLElement, user: UserConfiguration) {
        const script = document.createElement('script');
        script.id = SCRIPT_ELEMENT_ID;
        script.defer = true;
        script.type = 'text/javascript';
        script.src = WIDGET_URL;

        script.onload = () => {
            if (this.$zoho?.salesiq) {
                this.$zoho.salesiq.afterReady = () => this.onWidgetReady(user);
            }
        };

        host.appendChild(script);
    }

    private insertWidgetPlaceholder(host: HTMLElement) {
        const placeholder = document.createElement('div');
        placeholder.id = WIDGET_ELEMENT_ID;
        host.appendChild(placeholder);
    }

    private onWidgetReady(user: UserConfiguration) {
        if (!user) {
            return;
        }

        const visitorKey = this.buildVisitorKey(user);

        // Same person as last time — leave any in-progress conversation intact.
        if (visitorKey && this.readVisitorKey() === visitorKey) {
            this.identify(user);
            return;
        }

        // Someone else: wipe the retained identity first, then identify. The reset has to land
        // before the new details are set, hence the callback rather than two independent timers.
        this.whenWidgetResettable(isResettable => {
            if (isResettable) {
                this.reset();
            } else {
                console.warn('Zoho SalesIQ widget unavailable; identifying visitor without a reset.');
            }

            this.identify(user);
            this.writeVisitorKey(visitorKey);
        });
    }

    private identify(user: UserConfiguration) {
        // Resolved once: the widget's API surface is the thing that may be missing, and it should
        // not be re-checked between setting the name and setting the email.
        const visitor = this.$zoho?.salesiq?.visitor;

        if (!visitor) {
            return;
        }

        const name = this.buildVisitorName(user);

        if (name) {
            visitor.name(name);
        }

        if (user.userName) {
            visitor.email(user.userName.trim());
        }
    }

    private reset() {
        this.$zoho?.salesiq?.reset?.();
    }

    /**
     * `afterReady` can fire before `$ZSIQWidget` exists, and a reset issued before the widget has
     * settled does not stick — so wait for it, then give it a moment.
     */
    private whenWidgetResettable(callback: (isResettable: boolean) => void) {
        const startedAt = Date.now();
        let timerId;

        const check = () => {
            if (window['$ZSIQWidget'] !== undefined) {
                clearTimeout(timerId);
                setTimeout(() => callback(true), WIDGET_RESET_DELAY_MS);
            } else if (Date.now() - startedAt < WIDGET_POLL_TIMEOUT_MS) {
                timerId = setTimeout(check, WIDGET_POLL_INTERVAL_MS);
            } else {
                clearTimeout(timerId);
                callback(false);
            }
        };

        check();
    }

    /**
     * Prefers the first/last name pair, falling back to `displayName`, so an incomplete profile
     * cannot send SalesIQ a name built out of `undefined`.
     */
    private buildVisitorName(user: UserConfiguration): string {
        const nameFromParts = [user.firstName, user.lastName]
            .map(part => (part || '').trim())
            .filter(part => part.length > 0)
            .join(' ');

        return nameFromParts || (user.displayName || '').trim();
    }

    private buildVisitorKey(user: UserConfiguration): string {
        const userName = (user.userName || '').trim().toLowerCase();
        return userName || (user.id ? `id:${user.id}` : '');
    }

    private readVisitorKey(): string {
        // Deliberately browser-wide (not the app's sessionStorage) to match the scope SalesIQ keeps
        // its own visitor identity in.
        return localStorage ? localStorage.getItem(SalesIqService.VisitorKeyStorageKey) : null;
    }

    private writeVisitorKey(visitorKey: string) {
        if (!localStorage) {
            return;
        }

        if (visitorKey) {
            localStorage.setItem(SalesIqService.VisitorKeyStorageKey, visitorKey);
        } else {
            localStorage.removeItem(SalesIqService.VisitorKeyStorageKey);
        }
    }
}
